/**
 * Connectivity + sync state, as a tiny observable store.
 *
 * WHY this exists separately from React state: `createApiClient`'s
 * `onRequestSettled` fires from inside the transport, and the outbox replays on
 * a `window.online` event — both far outside any component. Both need somewhere
 * to publish that a `useSyncExternalStore` subscriber can read.
 *
 * The `SyncState` union and its precedence rule (a CONFLICT outranks everything
 * because only a human can clear it) come from `@sonauto/offline-sync`, so the
 * POS and the Staff App cannot disagree about what "đã đồng bộ" means.
 *
 * The one rule this store enforces: the pill it feeds must tell the employee
 * the TRUTH about what has and has not reached the server. "Đã đồng bộ" while
 * three attendance events sit in IndexedDB is how someone ends up unpaid.
 */
import { syncStateLabel, type SyncState } from '@sonauto/offline-sync';

export type { SyncState } from '@sonauto/offline-sync';
export { syncStateLabel } from '@sonauto/offline-sync';

export interface SyncStatus {
  /** `navigator.onLine`, kept live. */
  readonly online: boolean;
  readonly state: SyncState;
  /** Rows waiting or in flight. */
  readonly pending: number;
  /** Retryable failures. */
  readonly failed: number;
  /** Permanent rejections a human must look at. */
  readonly conflicts: number;
  /** Epoch ms of the last successful replay, or null. */
  readonly lastSyncedAt: number | null;
}

let status: SyncStatus = {
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  state: typeof navigator === 'undefined' || navigator.onLine ? 'ONLINE' : 'OFFLINE',
  pending: 0,
  failed: 0,
  conflicts: 0,
  lastSyncedAt: null,
};

type Listener = () => void;
const listeners = new Set<Listener>();

function set(patch: Partial<SyncStatus>): void {
  const next = { ...status, ...patch };
  if (
    next.online === status.online &&
    next.state === status.state &&
    next.pending === status.pending &&
    next.failed === status.failed &&
    next.conflicts === status.conflicts &&
    next.lastSyncedAt === status.lastSyncedAt
  ) {
    return;
  }
  status = next;
  for (const listener of listeners) listener();
}

export function subscribeSync(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getSyncStatus(): SyncStatus {
  return status;
}

/** Called by the outbox after every refresh. */
export function publishOutbox(input: {
  pending: number;
  failed: number;
  conflicts: number;
  lastSyncedAt: number | null;
  state: SyncState;
}): void {
  set(input);
}

/** Called by the api client for every settled request. */
export function markSyncSettled(ok: boolean): void {
  if (ok) set({ online: true });
}

export function setOnline(online: boolean): void {
  set({ online, state: online ? (status.state === 'OFFLINE' ? 'ONLINE' : status.state) : 'OFFLINE' });
}

/** Vietnamese label for the pill, from the shared package. */
export function currentSyncLabel(): string {
  return syncStateLabel(status.state);
}

/** Wired once from main.tsx. */
export function installConnectivityListeners(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('online', () => setOnline(true));
  window.addEventListener('offline', () => setOnline(false));
}
